import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { Textarea } from "@arcadeai/design-system/components/ui/atoms/textarea";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useUpdateProject } from "../api/hooks";
import {
  type CreateProjectFormData,
  CreateProjectSchema,
  type Project,
} from "../types";

export const UpdateProjectModal = ({
  project,
  setIsOpen,
}: {
  project: Project;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { organizationId } = useOrgProjectRouteContext();
  const { isPending: isUpdating, mutateAsync: updateProject } =
    useUpdateProject(organizationId);
  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(CreateProjectSchema),
    defaultValues: {
      name: project.name,
      description: project.description || "",
    },
  });

  const onSubmit = async (data: CreateProjectFormData) => {
    await updateProject({
      project_id: project.project_id,
      name: data.name,
      description: data.description,
    });
    setIsOpen(false);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogDescription>Update the project details below</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form className="mt-4 space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="My Awesome Project"
                    {...field}
                    autoFocus
                    disabled={isUpdating}
                  />
                </FormControl>
                <FormDescription>
                  Choose a unique name for your project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (Optional)</FormLabel>
                <FormControl>
                  <Textarea
                    className="resize-none"
                    placeholder="Describe what this project is for..."
                    rows={3}
                    {...field}
                    disabled={isUpdating}
                  />
                </FormControl>
                <FormDescription>
                  A brief description to help team members understand the
                  project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="flex justify-end gap-2">
            <Button
              disabled={isUpdating}
              onClick={() => setIsOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isUpdating} type="submit">
              {!!isUpdating && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Project
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
